import { ReactNode, useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutGrid,
  LineChart,
  Server,
  Bell,
  FileText,
  Bot,
  Scan,
  CheckSquare,
  Shield,
  ClipboardList,
  Download,
  Settings,
  LogOut,
  Search,
  PanelLeft,
  Plus,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import "@/styles/dashboard-chrome.css";
import "@/styles/dashboard-pages.css";

interface DashboardLayoutProps {
  children: ReactNode;
}

const NAV = [
  { url: "/dashboard", icon: LayoutGrid, label: "Dashboard" },
  { url: "/metrics", icon: LineChart, label: "Metrics" },
  { url: "/servers", icon: Server, label: "Servers" },
  { url: "/alerts", icon: Bell, label: "Alerts" },
  { url: "/logs", icon: FileText, label: "Logs" },
  { url: "/llm-monitoring", icon: Bot, label: "LLM Monitoring" },
  { url: "/scanners", icon: Scan, label: "Scanners" },
  { url: "/tasks", icon: CheckSquare, label: "Tasks" },
  { url: "/audit-trail", icon: Shield, label: "Audit Trail" },
  { url: "/reports", icon: ClipboardList, label: "Reports" },
  { url: "/install", icon: Download, label: "Install Agents" },
  { url: "/settings", icon: Settings, label: "Settings" },
];

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const { profile, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  // Force dark mode while in dashboard (matches the design)
  useEffect(() => {
    const root = document.documentElement;
    const had = root.classList.contains("dark");
    root.classList.add("dark");
    return () => {
      if (!had) root.classList.remove("dark");
    };
  }, []);

  const current = NAV.find((n) => location.pathname.startsWith(n.url));
  const crumb = current?.label || "Dashboard";

  const initials =
    (profile?.full_name || profile?.email || "U")
      .split(/\s+|@/)
      .filter(Boolean)
      .slice(0, 2)
      .map((s: string) => s[0]?.toUpperCase())
      .join("") || "U";

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  return (
    <div className="dash-root">
      <div className="page-bg" />
      <div className="grid-bg" />
      <div className="noise" />
      <div className="dash-app" style={collapsed ? { gridTemplateColumns: "72px 1fr" } : undefined}>
        <aside className="sidebar">
          <div className="sidebar__brand">
            <div className="dash-brand-logo" />
            {!collapsed && (
              <div className="sidebar__brand-meta">
                <b>ServSpect</b>
                <span>{profile?.tenants?.name?.toUpperCase() || "TENANT"}</span>
              </div>
            )}
            <button
              className="sidebar__brand-toggle"
              onClick={() => setCollapsed((c) => !c)}
              aria-label="Toggle sidebar"
            >
              <PanelLeft size={14} />
            </button>
          </div>
          {!collapsed && <div className="sidebar__label">Navigation</div>}
          <nav className="sidebar__nav">
            {NAV.map((n) => {
              const Ic = n.icon;
              return (
                <NavLink
                  key={n.url}
                  to={n.url}
                  className={({ isActive }) => "nav-item" + (isActive ? " active" : "")}
                  title={collapsed ? n.label : undefined}
                >
                  <Ic />
                  {!collapsed && <span>{n.label}</span>}
                </NavLink>
              );
            })}
          </nav>
          <div className="sidebar__user">
            <div className="avatar">{initials}</div>
            {!collapsed && (
              <div className="sidebar__user-meta">
                <b>{profile?.full_name || profile?.email || "User"}</b>
                <span>{(profile?.role || "USER").toUpperCase()}</span>
              </div>
            )}
            <button className="icon-btn" onClick={handleSignOut} aria-label="Sign out">
              <LogOut size={14} />
            </button>
          </div>
        </aside>

        <div className="dash-main">
          <header className="topbar">
            <button
              className="icon-btn"
              onClick={() => setCollapsed((c) => !c)}
              aria-label="Toggle sidebar"
            >
              <PanelLeft size={16} />
            </button>
            <div className="topbar__crumbs">
              <span>{profile?.tenants?.name?.toUpperCase() || "TENANT"}</span>
              <span>/</span>
              <b>{crumb}</b>
            </div>
            <div className="topbar__search">
              <Search size={14} />
              <input placeholder="Search servers, logs, alerts..." />
              <span className="topbar__kbd">⌘K</span>
            </div>
            <div className="topbar__actions">
              <button className="icon-btn" onClick={() => navigate("/alerts")} aria-label="Alerts">
                <Bell size={16} />
              </button>
              <button className="btn" onClick={() => navigate("/install")}>
                <Plus size={14} /> Add server
              </button>
            </div>
          </header>
          <div className="dash-content">{children}</div>
        </div>
      </div>
    </div>
  );
}
